import { PAY_TYPE } from '@/constants';
import { Review } from '@/apis';

// 기본 타입 정의
export const SHIPPING_TYPE = {
  GENERAL: 'general',
  REMOTE: 'remote',
  JEJU: 'jeju',
} as const;
export type ShippingType = (typeof SHIPPING_TYPE)[keyof typeof SHIPPING_TYPE];

export type PayType = 'card' | 'naver_pay' | 'cash' | 'phone' | 'toss_pay' | 'kakao_pay';
export type PaymentMethod = 'CARD' | 'NAVER_PAY' | 'KAKAO_PAY' | 'TOSS_PAY' | 'VIRTUAL_ACCOUNT';

export enum OrderStatus {
  UNPAID = 'unpaid',
  PAID = 'paid',
  PART_CANCELED = 'part_canceled',
  CANCELED = 'canceled',
  CANCEL_WAITING = 'cancel_waiting',
}

export enum OrderlineStatus {
  PAID = 'paid',
  UNPAID = 'unpaid',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  CANCEL_WAITING = 'cancel_waiting',
  CANCELED = 'canceled',
  PREPARING = 'preparing',
}

export type OrderlineStatusType = OrderlineStatus;

// 공통 인터페이스
interface CommonOrderInfo {
  buyer_email: string;
  buyer_name: string;
  buyer_phone: string;
  receiver_address: string;
  receiver_detail_address: string;
  receiver_name: string;
  receiver_phone: string;
  receiver_postcode: string;
  delivery_message: string;
  shipping_type: ShippingType;
}

export interface OrderLineWithId {
  main_option_id: number | null;
  sub_option_id: number | null;
  additional_product_id: number | null;
  quantity: number;
}

export interface CreateOrderParameters extends CommonOrderInfo {
  paymentMethod: PaymentMethod;
  order_data: {
    campaign_id: number;
    orderlines: OrderLineWithId[];
  }[];
  cash_receipt_type: string | null;
  cash_receipt_number: string | null;
  is_cash: boolean;
  tax_free_price: number;
  from_cart?: boolean;
}

export interface OrderSheetParameter {
  seller?: string;
  market_id: number;
  shipping_type: ShippingType;
  order_data: {
    campaign_id: number;
    orderlines: OrderLineWithId[];
  }[];
}

export type InterestFreeCardsType = {
  company_code: string;
  issuer_code: string;
  due_date: string;
  installment_free_months: number[];
  minimum_payment_amount: number;
};

export interface OrderData {
  campaign_id: number;
  product_price: number;
  orderlines: OrderLineWithId[];
}

export interface OrderDataList {
  delivery_policy_id: number;
  total_price: number;
  product_price: number;
  shipping_fee: number;
  discount_price: number;

  order_data: OrderData[];
}

export interface OrderSheetResponse {
  total_price: number;
  shipping_fee: number;
  discount_price: number;
  tax_free_price: number;
  interest_free_cards: InterestFreeCardsType[];
  order_data_list: OrderDataList[];
}

export class CashReceiptUsage {
  static 소득공제용 = true as const;
  static 지출증빙용 = false as const;
  static 미발행: null = null;
}

export type 현금영수증_발행용도 = boolean | null;

export type ImageType = {
  image: string;
  sequence: number;
};

interface CashReceiptInfo {
  brand_info: {
    business_number: string;
    ceo_name: string;
    company_name: string;
    location: string;
    tel: string;
  };
  cash_receipts: {
    amount: number;
    cash_receipt_type: string;
    created_at: string;
    id: number;
    identity_number: string;
    confirm_number: string;
    supply_amount: number;
    tax_amount: number;
    trade_type: 'issue' | 'cancel' | 'issue_fail';
  }[];
}

export interface DetailedOrderLine {
  additional_option_name: string | null;
  additional_product_name: string | null;
  campaign_id: number;
  campaign_image: {
    image: string;
    sequence: number;
  }[];
  campaign_name: string;
  cancel_reason: string | null;
  cancel_requested_at: string | null;
  canceled_at: string | null;
  cs_channel: string;
  invoice: {
    id: number;
    invoice_number: string;
    company: string;
  } | null;
  main_option_name: string;
  orderline_id: number;
  orderline_number: string;
  unit_price: number;
  quantity: number;
  review: {
    id: number;
  };
  status: OrderlineStatus;
  sub_option_name: string | null;
}

export interface Orders {
  order_number: string;
  status: string;
  campaign_name: string | null;
  product_price: number;
  shipping_fee: number;
  total_price: number;
  writable_review: boolean;
  review: Review;
  orderlines: DetailedOrderLine[];
}

export interface OrdersDetailResponse extends CommonOrderInfo {
  account?: string;
  amount?: number;
  bank_name?: string;
  cancel_price: number;
  cash_receipt_info?: CashReceiptInfo;
  cash_receipt_url?: string;
  cash_receipt_number: string | null;
  cash_receipt_type: string | null;
  created_at: string;
  cs_channel: string;
  customer: string;
  discount_price: number | null;
  expire_date?: string;
  id: number;
  initial_total_price: number;
  order_group_number: string;
  orders: Orders[];
  owner_username: string;
  paid_at: string;
  payment_info: {
    pay_type: keyof typeof PAY_TYPE;
    card_issuer: string | null;
    installment_plan_months: number | null;
  };
  product_price: number;
  receipt_url: string | null;
  shipping_fee: number;
  shipping_type: ShippingType;
  total_price: number;
  campaign_name: string;
}

export interface OrderCalculateRefundResponse {
  is_full_cancel: boolean;
  cancel_data: {
    cancel_amount: number;
    total_product_price: number;
    current_shipping_fee: number;
    additional_shipping_fee: number;
    discount_price: number | null;
    exclude_from_additional_shipping: boolean; // 복합과세인 경우 취소하려는 orderline이 모두 면세면 취소 불가
  };
}

type OrderItemType = {
  order_number: string;
  campaign_name: string;
  total_quantity: number;
  total_price: number;
  paid_at: string;
  status: OrderStatus;
  canceled_at: string;
  seller_username: string;
  images: ImageType[];
  writable_review: boolean;
  review: Review;
};

export type GetOrdersResponse = OrderItemType[];

export interface OrderDeliveryResponse {
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  receiver_detail_address: string;
  receiver_postcode: string;
  delivery_message: string;
  shipping_type: ShippingType;
}

export interface OrderGroup {
  id: number;
  name: string;
  orderline_count: number;
  order_group_number: string;
  total_price: number;
  created_at: string;
  status: OrderStatus;
  image: string;
}
